import React, { useState } from 'react';
import { JobClass, CharacterClass } from '../../../../shared/CharacterTypes';
import { JOB_ADVANCEMENTS, getJobAdvancement } from '../../../../shared/CharacterClasses';
import { panelSurface } from '../../theme/helpers';
import { COLORS, SPACE, FONT_SIZE, FONT_WEIGHT, RADII } from '../../theme';

interface MiniJobDisplayProps {
  currentJob?: JobClass;
  level: number;
  onOpenJobAdvancement: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const MiniJobDisplay: React.FC<MiniJobDisplayProps> = ({
  currentJob,
  level,
  onOpenJobAdvancement,
  className = '',
  style = {}
}) => {
  const currentJobConfig = currentJob ? JOB_ADVANCEMENTS[currentJob] : null;
  // Note: Simplified assumption about class for next job lookup.
  // In a real scenario, we might need the actual class.
  // But for the mini display, we can just look up based on Warrior for now as it was in the original code,
  // or maybe better, try to infer it.
  // The original code had: const nextJob = currentJob ? getJobAdvancement(CharacterClass.WARRIOR, level + 1) : null;
  // I will leave it as is to match original behavior, but add a comment.
  const nextJob = currentJob ? getJobAdvancement(CharacterClass.WARRIOR, level + 1) : null;

  const containerStyle: React.CSSProperties = {
    ...panelSurface(),
    padding: SPACE.lg,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    ...style
  };

  const [isHovered, setIsHovered] = useState(false);

  const hoverStyle: React.CSSProperties = isHovered ? {
    borderColor: COLORS.brand,
    transform: 'translateY(-2px)'
  } : {};

  const getJobIcon = (job?: JobClass): string => {
    if (!job) return '✨';

    switch (job) {
    case JobClass.WARRIOR_BASIC:
    case JobClass.SWORDSMAN:
    case JobClass.CRUSADER:
    case JobClass.HERO:
      return '⚔️';
    case JobClass.MAGE_BASIC:
    case JobClass.WIZARD:
    case JobClass.ARCHMAGE:
      return '🔮';
    case JobClass.RANGER_BASIC:
    case JobClass.HUNTER:
    case JobClass.RANGER_LORD:
      return '🏹';
    case JobClass.PALADIN_BASIC:
    case JobClass.KNIGHT:
    case JobClass.PALADIN_LORD:
      return '🛡️';
    default:
      return '✨';
    }
  };

  return (
    <div
      className={`mini-job-display ${className}`}
      style={{ ...containerStyle, ...hoverStyle }}
      onClick={onOpenJobAdvancement}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: SPACE.md
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md }}>
          <div aria-hidden style={{
            fontSize: FONT_SIZE.xl,
            color: COLORS.brandAccent
          }}>
            {getJobIcon(currentJob)}
          </div>
          <div>
            <div style={{
              fontSize: FONT_SIZE.md,
              color: COLORS.textPrimary,
              fontWeight: FONT_WEIGHT.bold
            }}>
              {currentJobConfig?.name || 'Basic'}
            </div>
            <div style={{
              fontSize: FONT_SIZE.xs,
              color: COLORS.textSecondary
            }}>
              Current Job
            </div>
          </div>
        </div>

        <div style={{
          background: `${COLORS.brand}33`,
          padding: `${SPACE.xxs}px ${SPACE.sm}px`,
          borderRadius: RADII.md,
          color: COLORS.brandAccent,
          fontSize: FONT_SIZE.xs,
          fontWeight: FONT_WEIGHT.bold
        }}>
          Lv.{level}
        </div>
      </div>

      {nextJob && JOB_ADVANCEMENTS[nextJob] && (
        <div style={{
          borderTop: `1px solid ${COLORS.borderSubtle}`,
          paddingTop: SPACE.sm,
          marginTop: SPACE.sm
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: FONT_SIZE.xs,
            color: COLORS.textSecondary
          }}>
            <span>Next: {JOB_ADVANCEMENTS[nextJob].name}</span>
            <span style={{ color: COLORS.brand }}>Lv.{JOB_ADVANCEMENTS[nextJob].requiredLevel}</span>
          </div>
        </div>
      )}
    </div>
  );
};
